// openapi-fetch wrapper for the Fancy API.
// Mirrors educator's `openapi-clients.ts` wrapper shape:
// megarepo/frontend/educator/entities/ts/openapi-clients.ts

import createClient from 'openapi-fetch'

import {PATHS} from '@frontrow/console/src/entities/helpers/paths'

import {type operations as fOps, type paths as fancyPaths} from './fancy-api/generated-client'
import {fancyApiFetch} from './middleware/credentials'

export type fancyOperations = fOps

/**
 * Convenience type for extracting the 200 JSON response body of a generated
 * `operations[<Op>]`. Mirrors educator's `FancyOk`.
 */
export type FancyOk<
  Op extends {responses: {200: {content: {'application/json': T}}}},
  T = Op['responses'][200]['content']['application/json']
> = T

/** The JSON request body of a generated `operations[<Op>]`. */
export type FancyBody<Op extends {requestBody?: {content: {'application/json': unknown}}}> =
  NonNullable<Op['requestBody']>['content']['application/json']

/**
 * The keys of `Body` that `Op` does not accept, for hand-written bodies that
 * cannot be `FancyBody`. Use with `AssertNoUnacceptedKeys`.
 */
export type UnacceptedKeys<
  Body,
  Op extends {requestBody?: {content: {'application/json': unknown}}}
> = Exclude<keyof Body, keyof FancyBody<Op>>

/**
 * `T` without its kebab-case keys. The API sends each of them twice during the
 * migration to camelCase, so a parser that reads only the camelCase key accepts
 * this narrower view.
 */
export type CamelCaseKeysOnly<T> = {
  [K in keyof T as K extends `${string}-${string}` ? never : K]: T[K]
}

/** Fails to compile, naming the keys, unless `Keys` is `never`. */
export type AssertNoUnacceptedKeys<Keys extends never> = Keys

const querySerializer = {
  array: {
    style: 'form' as const,
    explode: false
  }
}

// `credentials: 'include'` is intentionally omitted here — `fancyApiFetch`
// already bakes it into every outgoing Request, so setting it here too would
// just be a second, easily-drifting source of truth for the same behavior.
export const consoleFancyClient = createClient<fancyPaths>({
  baseUrl: PATHS.unversionedAPIUrl,
  fetch: fancyApiFetch,
  querySerializer
})

/**
 * Narrows openapi-fetch's `{ data: T | undefined, error, response }` result
 * down to `T`. `fancyApiFetch` already throws before openapi-fetch returns
 * for any non-2xx response, so `data` is always defined here in practice —
 * this exists to satisfy the type system without an unsafe cast at every call
 * site. Mirrors educator's `extractData`.
 */
export function extractData<T>({data}: {data?: T}): T {
  if (data === undefined) {
    throw new Error('Expected a response body but got none')
  }
  return data
}
